import { RootState } from "../../../interfaces/root-state.interface";
import { setIsLoading } from "../../../store/actions/dateRange.actions";
import {
    AlertTypes,
    apiClient,
    Grid,
    IAlertData,
    setAlert,
    Paper,
    Typography,
    Skeleton,
    useTheme
} from "@leap4/frontend";
import {connect, ConnectedProps} from "react-redux";
import {IDateRangeGState, ILoadingComponents} from "../../../interfaces/datesGlobalState.interface";
import React, {useCallback, useEffect, useState} from "react";
import {ComparisonState, HomeUrl} from "../../../config/generals";
import {useAppDispatch, useRefreshInterval} from "../../../store/hooks";
import ReportHeader from "../common/ReportHeader/ReportHeader";
import {HomeGeneralInfo} from "./components/homeGeneralInfo";
import {AdServerCards} from "./components/AdServerCards/AdServerCards";
import {getDefaultTimeDisplay, indexToTimeInterval} from "../common/charts/charts";
import HomeTopTagsTable from "./components/topCombosTable/HomeTopTagsTable";
import ComparisonOverviewGraph, { IOverviewGraphResponse } from "../common/ComparisonGraph";
import { MetricsComparison } from "../../shared/MetricsComparison/MetricsComparison.container";
import {
    IHomepageOverviewDates,
    IHomePageOverviewPartnerDates,
    IHomepageOverviewResult, ITopPartnersRangeCombo,
    ITopTagsRangeCombo
} from "./interfaces";
import { metricsComparisonKeyMap } from "../../shared/MetricsComparison/MetricsComparisonHelpers";
import { PredictiveGraphContainer } from "../common/PredictiveGraph/PredictiveGraph.container";
import { PartnerCards } from "./components/PartnerCards/PartnerCards";
import { LastUpdatedAt } from "../common/LastUpdatedAt/LastUpdatedAt";
import HomeTopPartnersTable from "./components/topPartnersCombosTable/HomeTopPartnersComboTable";
import moment from "moment";
import { checkIfDateIsInComparisonOptions, getComparisonOptionsDates } from "../common/time.utils";
import { getUpdatedAt } from "./api-utils";

const mapStateToProps = (state: RootState) => ({
    dates: state.dates,
    selectedOrganization: state.auth.selectedOrganizationId
});

const mapActions = {
    setIsLoading,
    setAlert
}

const connector = connect(mapStateToProps, mapActions);

type PropsFromRedux = ConnectedProps<typeof connector>;

export interface IHomeProps extends PropsFromRedux {
    dates: IDateRangeGState
    setIsLoading: (isLoading: Partial<ILoadingComponents>) => void
    setAlert: (alert: IAlertData) => void
}

const HomePage = (props: IHomeProps): JSX.Element => {
    const theme = useTheme()
    const { dates, setIsLoading, setAlert, selectedOrganization } = props;
    const { startDate, endDate, isLoading, isLoadingAny, selectedComparison } = dates;
    const [updatedAt, setUpdatedAt] = useState<Date>(null)
    const [data, setData] = useState<IHomepageOverviewResult>(null);
    const [adServers, setAdServers] = useState<IHomepageOverviewDates>(null);
    const [partners, setPartners] = useState<IHomePageOverviewPartnerDates>(null);
    const [topTags, setTopTags] = useState<ITopTagsRangeCombo>(null);
    const [topPartnersCombos, setTopPartnersCombos] = useState<ITopPartnersRangeCombo>(null);
    const [graphData, setGraphData] = useState<IOverviewGraphResponse>(null)
    const [adServersForCards, setAdServersForCards] = useState([])
    const [graphInterval, setGraphInterval] = useState<number>(getDefaultTimeDisplay(startDate, endDate))
    const [specificComparisonDate, setSpecificComparisonDate] = useState()
    const [optionalComparisonDates, setOptionalComparisonDate] = useState<any>(getComparisonOptionsDates(startDate, endDate))
    const [comparisonData, setComparisonData] = useState<ComparisonState>({
        compare: selectedComparison,
        data: null
    })

    useEffect(() => {
        if (specificComparisonDate && !checkIfDateIsInComparisonOptions(specificComparisonDate, optionalComparisonDates)) {
            fetchGeneralData()
            //fetchGraphData()
        }
    }, [specificComparisonDate])


    const dispatch = useAppDispatch();
    useRefreshInterval(() => {
        if (dates.startDate === dates.endDate && dates.startDate === moment().format("YYYY-MM-DD")) {
            //fetchGraphData()
            fetchGeneralData()
        }
    })

    // Helper function to handle cache-related errors
    const handleCacheError = (err: any, dataType: string) => {
        const errorResponse = err?.response?.data;
        if (err?.response?.status === 503) {
            const errorCode = errorResponse?.error;
            if (errorCode === 'CACHE_NOT_AVAILABLE' || errorCode === 'CACHE_STALE') {
                const cacheAge = errorResponse?.details?.cacheAgeMinutes;
                const msg = errorCode === 'CACHE_NOT_AVAILABLE'
                    ? `${dataType} cache is not available. The cache service may not be running. Please contact support.`
                    : `${dataType} cache is outdated (${cacheAge} minutes old). The cache service may not be running properly. Please contact support.`;

                setAlert({
                    msg,
                    type: AlertTypes.FAILURE,
                    d: dispatch
                });
                return true;
            }
        }
        return false;
    };

    const refreshTopTags = async () => {
        try {

            const response = await apiClient.post(`${HomeUrl}/topTags`, {
                startDate,
                endDate,
                specificComparisonDate: specificComparisonDate || null
            });

            const refreshedTopTags: ITopTagsRangeCombo = response.data;
            setIsLoading({ isLoadingTopTags: true });
            setTopTags(refreshedTopTags);
        } catch (err: any) {
            if (!handleCacheError(err, "Top Tags")) {
                setAlert({ msg: "Could not refresh top tags", type: AlertTypes.FAILURE, d: dispatch });
            }
        } finally {
            setIsLoading({ isLoadingTopTags: false });
        }
    };
    async function fetchIntermediateData(lastUpdatedAt: Date) {
        let savedLastUpdatedAt = new Date(sessionStorage.getItem('updatedAt'));
        let savedLastUpdatedDate = savedLastUpdatedAt.toISOString().slice(0, 10);
        let newLastUpdatedDate = lastUpdatedAt.toISOString().slice(0, 10);
        let savedAdServers = JSON.parse(sessionStorage.getItem('adServers'));
        //let savedPartners = JSON.parse(sessionStorage.getItem('partners'));
        let savedGraph = JSON.parse(sessionStorage.getItem('graph'));
        let savedTopTags = JSON.parse(sessionStorage.getItem('topTags'));
        let savedTopPartnersCombos = JSON.parse(sessionStorage.getItem('topPartnersCombos'));

        //console.log("LastUpdateAt: ", lastUpdatedAt);
        //console.log("SavedLastUpdateAt: ", savedLastUpdatedAt);
        //console.log("SavedLastUpdateAtDate: ", savedLastUpdatedDate);
        //console.log("startDate: ", startDate);
        //console.log("endDateDate: ", endDate);

        await fetchGraphAndOverview();

        if (lastUpdatedAt.getTime() === savedLastUpdatedAt.getTime() && savedLastUpdatedDate === startDate && startDate === endDate
            && savedGraph) {
            setGraphData(savedGraph);
            setIsLoading({ isLoadingGraph: false });
        }

        if (lastUpdatedAt.getTime() === savedLastUpdatedAt.getTime() && savedLastUpdatedDate === startDate && startDate === endDate
            && savedTopTags) {
            setTopTags(savedTopTags);
            setIsLoading({ isLoadingTopTags: false });
        } else if (newLastUpdatedDate === startDate && startDate === endDate) {
            await fetchTopTags(true);
        } else {
            await fetchTopTags(false);
        }

        if (lastUpdatedAt.getTime() === savedLastUpdatedAt.getTime() && savedLastUpdatedDate === startDate && startDate === endDate
            && savedTopPartnersCombos) {
            setTopPartnersCombos(savedTopPartnersCombos);
            setIsLoading({ isLoadingTopPartnersCombos: false });
        } else if (newLastUpdatedDate === startDate && startDate === endDate) {
            await fetchTopPartnersCombos(true);
        } else {
            await fetchTopPartnersCombos(false);
        }

        /* if(lastUpdatedAt.getTime() === savedLastUpdatedAt.getTime() && savedLastUpdatedDate === startDate && startDate === endDate
                 && savedPartners){
             setPartners(savedPartners);
             setIsLoading({isLoadingPartners: false});
         } else if (newLastUpdatedDate === startDate && startDate === endDate){
             await fetchPartners(true);
         } else {
             await fetchPartners(false);
         }*/


        async function fetchGraphAndOverview() {
            if (lastUpdatedAt.getTime() === savedLastUpdatedAt.getTime() && savedLastUpdatedDate === startDate && startDate === endDate
                && savedAdServers && savedGraph) {
                setAdServers(savedAdServers);
                setGraphData(savedGraph);
                setIsLoading({ isLoadingGraph: false })
                setIsLoading({ isLoadingAdServers: false });
            } else if (newLastUpdatedDate === startDate && startDate === endDate) {
                await Promise.all([
                    fetchAdServers(true),
                    fetchGraph(true)
                ]);
            } else {
                await Promise.all([
                    fetchAdServers(false),
                    fetchGraph(false)
                ]);
            }
        }

    }

    const fetchGeneralData = () => {
        //setIsLoading({isLoadingGeneral: true});
        setIsLoading({ isLoadingAdServers: true });
        setIsLoading({ isLoadingGraph: true });
        setIsLoading({ isLoadingTopTags: true });
        setIsLoading({ isLoadingPartners: true });
        setIsLoading({ isLoadingTopPartnersCombos: true });

        (async () => {
            try {
                let updatedAt = await fetchUpdatedAt();
                await fetchIntermediateData(updatedAt);

                // in the end we store the last updatedAt date
                sessionStorage.setItem('updatedAt', updatedAt.toISOString());
            } catch (err) {
                sessionStorage.clear();
                console.log(err);
                setAlert({ msg: "Couldnt fetch data", type: AlertTypes.FAILURE, d: dispatch })
            } finally {
                //setIsLoading({isLoadingGeneral: false});
                setIsLoading({ isLoadingAdServers: false });
                setIsLoading({ isLoadingGraph: false });
                setIsLoading({ isLoadingTopTags: false });
                setIsLoading({ isLoadingPartners: false });
                setIsLoading({ isLoadingTopPartnersCombos: false });
            }
        })()
    };

    async function fetchUpdatedAt() {
        const updatedAtResponse = await getUpdatedAt(startDate, endDate, specificComparisonDate);
        setUpdatedAt(updatedAtResponse);
        return updatedAtResponse;
    }

    async function fetchAdServers(shouldSaveToSessionStore: boolean) {
        const baseUrl = `${HomeUrl}/overview/adServers`;
        try {
            const apiResponse = await apiClient.post(baseUrl, {
                startDate,
                endDate,
                specificComparisonDate: specificComparisonDate || null,
            });
            const adServersResponse = apiResponse.data;
            setIsLoading({ isLoadingAdServers: true });
            setAdServers(adServersResponse);
            if (shouldSaveToSessionStore) {
                sessionStorage.setItem('adServers', JSON.stringify(adServersResponse));
            }

            setIsLoading({ isLoadingAdServers: false });
            return adServersResponse;
        } catch (err: any) {
            if (handleCacheError(err, "Ad Servers data")) {
                // Leave adServers as null so UI shows "Error with getting the data..."
                setIsLoading({ isLoadingAdServers: false });
                return null;
            }
            throw err;
        }
    }

    async function fetchPartners(shouldSaveToSessionStore: boolean) {
        const baseUrl = `${HomeUrl}/overview/partners`;
        try {
            const apiResponse = await apiClient.post(baseUrl, {
                startDate,
                endDate,
                specificComparisonDate: specificComparisonDate || null,
            });
            const partnersResponse = apiResponse.data;
            setIsLoading({ isLoadingPartners: true });
            setPartners(partnersResponse);
            if (shouldSaveToSessionStore) {
                sessionStorage.setItem('partners', JSON.stringify(partnersResponse));
            }

            setIsLoading({ isLoadingPartners: false });
            return partnersResponse;
        } catch (err: any) {
            if (handleCacheError(err, "Partners data")) {
                setIsLoading({ isLoadingPartners: false });
                return null;
            }
            throw err;
        }
    }

    async function fetchTopTags(shouldSaveToSessionStore: boolean) {
        const baseUrl = `${HomeUrl}/overview/topTags`;
        try {
            const apiResponse = await apiClient.post(baseUrl, {
                startDate,
                endDate,
                specificComparisonDate: specificComparisonDate || null,
            });
            const topTagsResponse = apiResponse.data;
            setTopTags(topTagsResponse);
            if (shouldSaveToSessionStore) {
                sessionStorage.setItem('topTags', JSON.stringify(topTagsResponse));
            }

            setIsLoading({ isLoadingTopTags: false });
            return topTagsResponse;
        } catch (err: any) {
            if (handleCacheError(err, "Top Tags data")) {
                setIsLoading({ isLoadingTopTags: false });
                return null;
            }
            throw err;
        }
    }

    async function fetchTopPartnersCombos(shouldSaveToSessionStore: boolean) {
        const baseUrl = `${HomeUrl}/overview/topPartnersCombos`;
        try {
            const apiResponse = await apiClient.post(baseUrl, {
                startDate,
                endDate,
                specificComparisonDate: specificComparisonDate || null,
            });
            const topPartnersCombosResponse = apiResponse.data;
            if (shouldSaveToSessionStore) {
                sessionStorage.setItem('topPartnersCombos', JSON.stringify(topPartnersCombosResponse));
            }
            setTopPartnersCombos(topPartnersCombosResponse);
            setIsLoading({ isLoadingTopPartnersCombos: false });
            return topPartnersCombosResponse;
        } catch (err: any) {
            if (handleCacheError(err, "Top Partners data")) {
                setIsLoading({ isLoadingTopPartnersCombos: false });
                return null;
            }
            throw err;
        }
    }

    async function fetchGraph(shouldSaveToSessionStore: boolean) {
        const baseUrl = `${HomeUrl}/overview/graph`
        try {
            const graphResponse = await apiClient.post(baseUrl, {
                startDate,
                endDate,
                specificComparisonDate,
                timeInterval: indexToTimeInterval(graphInterval)
            })
            const currGraphData: IOverviewGraphResponse = graphResponse.data;
            setGraphData(currGraphData);
            if (shouldSaveToSessionStore) {
                sessionStorage.setItem('graph', JSON.stringify(currGraphData));
            }
            setIsLoading({ isLoadingGraph: false });
            return currGraphData;
        } catch (err: any) {
            if (handleCacheError(err, "Graph data")) {
                setIsLoading({ isLoadingGraph: false });
                return null;
            }
            throw err;
        }
    }

    const fetchGraphData = () => {
        (async () => {
            let lastUpdatedAt = await fetchUpdatedAt();
            let newLastUpdatedDate = lastUpdatedAt.toISOString().slice(0, 10);
            let savedLastUpdatedAt = new Date(sessionStorage.getItem('updatedAt'));
            let savedLastUpdatedDate = savedLastUpdatedAt.toISOString().slice(0, 10);
            let savedGraph = JSON.parse(sessionStorage.getItem('graph'));

            if (lastUpdatedAt.getTime() === savedLastUpdatedAt.getTime() && savedLastUpdatedDate === startDate && startDate === endDate
                && savedGraph) {
                setGraphData(savedGraph);
                setIsLoading({ isLoadingGraph: false });
            } else {
                try {
                    const baseUrl = `${HomeUrl}/overview/graph`
                    const graphResponse = await apiClient.post(baseUrl, {
                        startDate,
                        endDate,
                        specificComparisonDate,
                        timeInterval: indexToTimeInterval(graphInterval)
                    })
                    const currGraphData: IOverviewGraphResponse = graphResponse.data
                    setIsLoading({ isLoadingGraph: true });
                    if (newLastUpdatedDate === startDate && startDate === endDate) {
                        sessionStorage.setItem('graph', JSON.stringify(currGraphData));
                    }

                    setGraphData(currGraphData)

                } catch (err) {
                    sessionStorage.removeItem('graph');
                    setAlert({ msg: "Could'nt fetch Graph Data", type: AlertTypes.FAILURE, d: dispatch })
                } finally {
                    setIsLoading({ isLoadingGraph: false });
                }
            }
        })()

    }

    const updateComparisonData = () => {
        const dataForComparison = adServers?.overviewTotals
        let comparisonData;

        switch (selectedComparison) {
            case 1:
                comparisonData = dataForComparison?.dayAgo?.totals
                break;
            case 7:
                comparisonData = dataForComparison?.weekAgo?.totals
                break;
            case 28:
                comparisonData = dataForComparison?.monthAgo?.totals
                break;
            case 30:
                comparisonData = dataForComparison?.fullMonthAgo?.totals
                break;
            case -1:
                comparisonData = dataForComparison?.specificComparisonDate?.totals
                break;
            default:
                comparisonData = dataForComparison?.selectedDates?.totals
                break;
        }
        setComparisonData({ compare: selectedComparison, data: comparisonData })
    }

    useEffect(() => {
        getAdServers(selectedComparison)
    }, [selectedComparison, adServers])

    const getAdServers = (newSelectedComparison) => {
        const res = adServers?.overviewTotals?.selectedDates?.adServers?.map((adSer: any) => {
            const compareData = adServers?.overviewTotals[metricsComparisonKeyMap[newSelectedComparison]]?.adServers?.find(adServer => {
                return adSer._id === adServer._id
            })?.totals
            const { totals, adServer } = adSer
            return {
                totals,
                adServer,
                compareData
            }
        })
        setAdServersForCards(res)
    }


    const updateTimeInterval = () => {
        const newInterval = getDefaultTimeDisplay(startDate, endDate);
        if (newInterval !== graphInterval) {
            setGraphInterval(getDefaultTimeDisplay(startDate, endDate))
        } else {
            fetchGraphData()
        }
    }

    useEffect(() => { fetchGeneralData(); }, [startDate, endDate, selectedOrganization]);
    useEffect(() => { updateTimeInterval(); }, [startDate, endDate, selectedOrganization]);
    useEffect(() => { fetchGraphData(); }, [graphInterval, selectedOrganization]);
    useEffect(() => { updateComparisonData(); }, [adServers, selectedComparison, selectedOrganization]);

    const ReportHeaderWithProps = () => (<ReportHeader breadcrumbs={[]} />)
    if (!isLoading.isLoadingAdServers && !adServers) return (<>
        <ReportHeaderWithProps />
        <Typography variant={"overline"}> Error with getting the
            data...</Typography>

    </>)
    return (
        <>
            <ReportHeaderWithProps />
            <>
                <Grid container justifyContent={"flex-start"} alignItems={"stretch"}
                      style={{
                          marginRight: "15px",
                          filter: theme.palette.mode === 'dark' ? 'drop-shadow(0px 0px 2px lightgray' : 'drop-shadow(0px 0px 5px lightgray'
                      }}
                      spacing={2}>
                    <Grid size={{ xs: 12, lg: 4 }} sx={{ flexShrink: 0, paddingLeft: { xs: 0, lg: '12px' } }}>
                        <Grid container direction={"column"} spacing={1} sx={{ height: '100%' }}>
                            <Grid size={12} height={"100%"} display={'flex'} flexDirection={'column'} gap={2} sx={{ minHeight: 0 }}>
                                <Grid size={12} sx={{ flex: 1, minHeight: 0 }}>
                                    <MetricsComparison optionalComparisonDates={optionalComparisonDates} specificComparisonDate={specificComparisonDate}
                                        setSpecificComparisonDate={setSpecificComparisonDate} />
                                </Grid>
                                {updatedAt && (
                                    <Grid size={12} sx={{ flex: 0 }}>
                                        <LastUpdatedAt updatedAt={updatedAt} />
                                    </Grid>
                                )}
                            </Grid>

                        </Grid>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 8 }} sx={{ flexShrink: 1, minWidth: 0, paddingRight: { xs: 0, lg: '12px' } }}>
                        {isLoading.isLoadingAdServers ?
                            <Skeleton variant={"rounded"} sx={{borderRadius: "16px"}} height={"100%"} width={"100%"}
                                      animation={"wave"}/> :
                            <Paper elevation={5} sx={{
                                padding: "20px",
                                boxShadow: 'none',
                                borderRadius: '16px',
                                height: '100%',
                                width: '100%',
                            }}
                            >
                                <HomeGeneralInfo data={adServers?.overviewTotals?.selectedDates?.totals}
                                                 comparisonData={comparisonData}/>
                            </Paper>}
                    </Grid>

                    <Grid item sx={{ height: '400px', margin: '0 auto', width: { xs: '95%', sm: '96%', md: '99%' } }}>
                        <ComparisonOverviewGraph isLoading={isLoading.isLoadingGraph}
                            graphData={graphData}
                            startDate={startDate}
                            endDate={endDate}
                            setNewInterval={setGraphInterval}
                            graphIntervalIndx={graphInterval} />


                    </Grid>
                </Grid>
                {isLoading.isLoadingTopTags ?
                    <Skeleton animation={"wave"} sx={{ borderRadius: "16px", m: 5 }} variant={"rounded"}
                        height={"600px"} /> :
                    <Grid sx={{ margin: '0 10px' }}>
                        <HomeTopTagsTable tags={topTags?.selectedDates || []}
                            isLoading={isLoading.isLoadingTopTags}
                            compareData={topTags?.[metricsComparisonKeyMap[comparisonData.compare]]}
                            refreshFunc={refreshTopTags}

                        />
                    </Grid>}
                {isLoading.isLoadingTopPartnersCombos ?
                    <Skeleton animation={"wave"} sx={{ borderRadius: "16px", m: 5 }} variant={"rounded"}
                        height={"600px"} /> :
                    <Grid sx={{ margin: '0 10px' }}>
                        <HomeTopPartnersTable
                            partners={topPartnersCombos?.selectedDates || []}
                            isLoading={isLoading.isLoadingTopPartnersCombos}
                            compareData={topPartnersCombos?.[metricsComparisonKeyMap[comparisonData.compare]]} />
                    </Grid>}
                <Grid container justifyContent={"center"}
                    style={{ marginBottom: '10px' }}
                    spacing={1}>
                    {isLoading.isLoadingPartners ?
                        <Skeleton animation={"wave"} sx={{ borderRadius: "16px" }} variant={"rounded"} height={"200px"}
                            width={"100%"} /> :
                        //    partners && Object.keys(partners?.overviewTotals).length > 0 ?
                        <Grid>
                            {<PartnerCards
                                isLoading={isLoading.isLoadingPartners}
                                specificComparisonDate={specificComparisonDate}
                                endDate={endDate}
                                newSelectedComparison={selectedComparison}
                                startDate={startDate}
                            />}
                        </Grid>
                        //: <Grid></Grid>
                    }

                </Grid>
                <Grid container justifyContent={"center"}
                    style={{ marginBottom: '10px', filter: 'drop-shadow(0px 0px 3px lightgray' }}
                    spacing={1}>
                    {isLoading.isLoadingAdServers ?
                        <Skeleton animation={"wave"} sx={{ borderRadius: "16px" }} variant={"rounded"} height={"200px"}
                            width={"100%"} /> :
                        <Grid>
                            <AdServerCards adServers={adServersForCards} isLoading={isLoading.isLoadingAdServers} />
                        </Grid>
                    }
                    <Grid
                        sx={{ height: '400px', margin: '0 auto', width: { xs: '95%', sm: '96%', md: '99%' } }}
                    >
                        <PredictiveGraphContainer url={`${HomeUrl}/overview/predictive-graph`}
                            organization={selectedOrganization} />
                    </Grid>
                </Grid>


            </>

        </>
    )

}

export default connector(HomePage)

